/* eslint-disable no-unused-vars */
const analyticsTypes = require('./jsdoc-types');
const Types = require('../../../../jsdoc-types');
/* eslint-enable no-unused-vars */

// EXPERIENCE_COMPONENT_TYPES is used for type definitions
const {
  EXPERIENCE_COMPONENT_TYPES, // eslint-disable-line no-unused-vars
  PAGE_TYPES,
  PRODUCT_TILE_LOCATION,
} = require('../../../../config/analytics');

const { default: getFrontConfig } = require('../common/getFrontConfig');
const { createEventBus } = require('../common/utils/eventBus');
const { generateSingletonExports } = require('../common/utils/singleton');

const FRONT_CONFIG = getFrontConfig();

/**
 * Pub/Sub events for target actions to collect data from
 */
const EVENTS = {
  /**
   * Event triggered when accessibility icon is clicked
   * Currently, active in US and PR sites
   * @param {object} payload The payload of the event
   * @param {string} payload.action - The click action: open/close
   * @param {PAGE_TYPES} payload.pageType - Page type where the icon is clicked
   */
  accessibilityIconClicked: 'analytics:accessibility-icon:clicked',
  /**
   * Event triggered when edit button of the address is clicked in Addresses list
   * in My Account.
   * @param {void} - No payload
   */
  accountAddressEdited: 'account:address:edited',
  /**
   * Event triggered when details of the order link is clicked in Order list
   * in My Account.
   * @param {void} - No payload
   */
  accountOrderDetailsClicked: 'account:orders:details:clicked',
  /**
   * Event triggered when return an item button is clicked in Order list
   * in My Account.
   * @param {void} - No payload
   */
  accountOrderReturnItemClicked: 'account:orders:return:clicked',
  /**
   * Event triggered when tracking link of the order is clicked in Order list
   * in My Account.
   * @param {void} - No payload
   */
  accountOrderTrackingLinkClicked: 'account:orders:traking:clicked',
  /**
   * Event triggered when edit button of the Personal details is clicked
   * in Addresses list in My Account.
   * @param {void} - No payload
   */
  accountPersonalDetailsEdited: 'account:personal:edited',
  /**
   * Event triggered when a new account is created.
   * There are 3 places where we can do it:
   * 1. Checkout Page
   * It's necessary to start the purchase process as a guest user
   * It happens in this URL: https://www.bimbaylola.com/es_es/checkout-login
   * The way to access the form is by clicking in "You can also REGISTER" link
   * 2. Login/Account Page
   * It happens in this URL: https://www.bimbaylola.com/es_es/cuenta/login?rurl=1
   * It's the form located at the bottom of this view.
   * 3. Thank you Page
   * It happens in this URL: https://www.bimbaylola.com/es_es/order-confirm
   * The way to access the form is by completing the purchase
   * @param {string} location - The page or section where the account is registered
   */
  accountRegistered: 'account:registered',
  /**
   * Event triggered when details of the return link is clicked in RMA list
   * in My Account.
   * @param {void} - No payload
   */
  accountReturnDetailsClicked: 'account:returns:details:clicked',
  /**
   * Event triggered when the ATC phone is clicked.
   * @param {string} location - The page or section where phone is clicked
   */
  atcPhoneClicked: 'atcPhone:clicked',
  /**
   * Event triggered when an image on the Birthday landing page, or the
   * download all button, is clicked to download it.
   * The code and translations of this landing is in these content assets:
   *  1. Code: birthday
   *  2. Styles: birthday-styles
   *  3. Translations: birthday-header
   * Access to this landing page: https://www.bimbaylola.com/es_es/birthday.html
   * @param {object} payload The payload of the event
   * @param {string} item - The image selected (or all of them)
   */
  birthdayLandingItemClicked: 'birthday-landing:item:clicked',
  /**
   * Event that is triggered after the shopping cart or mini-cart page
   * is opened while empty.
   * @param {void} - No payload
   */
  cartEmptyViewed: 'cart:empty:viewed',
  /**
   * Event triggered when chat pop-up is opened.
   * @param {void} - No payload
   */
  chatOpened: 'chat:opened',
  /**
   * Event triggered after the cart page or minicart are opened.
   * First step of the checkout funnel.
   * @param {void} - No payload
   */
  checkoutCartViewed: 'checkout:cart:viewed',
  /**
   * Event triggered after edit link is clicked in Checkout order summary.
   * @param {object} payload The payload of the event
   * @param {string} payload.item Section edited by clicking on link
   */
  checkoutOrderSummaryEdited: 'checkout:order:summary',
  /**
   * Event triggered after submitted payment info and moved to next step.
   * Fourth step of the checkout funnel.
   * @param {object} payload The payload of the event
   * @param {Types.Order} payload.order - The order info
   * @param {Types.FullProduct} payload.product The full product data
   */
  checkoutPaymentInfoAdded: 'checkout:payment:added',
  /**
   * Event triggered after logged to beginning the checkout.
   * Second step of the checkout funnel.
   * @param {object} [payload] The payload of the event
   * @param {Types.Order} [payload.order] - The order info
   */
  checkoutProcessStarted: 'checkout:process:started',
  /**
   * Event triggered after creating the new order and visiting the confirmation
   * page.
   * @param {object} payload The payload of the event
   * @param {Types.Order} payload.order - The order info
   */
  checkoutPurchaseConfirmed: 'checkout:purchase',
  /**
   * Event triggered after submitted shipping info and moved to next step.
   * Third step of the checkout funnel.
   * @param {object} payload The payload of the event
   * @param {Types.Order} payload.order - The order info
   * @param {Types.FullProduct} payload.product The full product data
   */
  checkoutShippingInfoAdded: 'checkout:shipping:added',
  /**
   * Event triggered when the user opens the contact form by means of email
   * @param {void} - No payload
   */
  contactFormOpened: 'contact:formOpened',
  /**
   * Event triggered when a coupon is applied in minicart, an error occurs and
   * error message is displayed
   * Different error codes are possible:
   * 1. Already applied: error.coupon.already.in.cart
   * 2. Already redeemed: error.coupon.already.redeemed
   * 3. Can't be combined: error.coupon.cannot.be.combined
   * 4. Expired: error.coupon.expired
   * 5. Unapplicable: error.coupon.not.applicable
   * 6. Invalid platform: error.coupon.invalid.platform
   * 7. Default/Unknown/Disabled/Inactive/Redemption limit exceeded: error.unable.to.add.coupon
   * @param {string} errorCode The error code
   */
  couponAppliedErrorCode: 'coupon:applied:errorCode',
  /**
   * Event triggered when a cross selling modal is opened.
   * The initial products are not likely loaded yet.
   * @param {Object} payload Event payload
   * @param {string} payload.categoryId The ID of the category of the related
   *  products
   */
  crossSellingGridOpened: 'analytics:cross-selling:grid-opened',
  /**
   *
   * Event triggered when the list of recomended products is rendered in a cross
   * selling modal
   * @param {Object} payload Event payload
   * @param {Types.Category} payload.category Category of the related products
   * @param {Object[]} payload.products Products listed
   * @param {Object} payload.products.display Display config for a product
   * @param {jsdocTypes.FullProduct} payload.products.product Products listed
   * @param {Object} payload.products.urls URLS of the product
   */
  crossSellingGridViewed: 'analytics:cross-selling:grid-viewed',
  /**
   * Event triggered when a site and a locale are submitted from market selector
   * form at Dispatcher landing page.
   * @param {object} payload The payload of the event
   * @param {string} payload.url Destination URL (format: domain/site_locale)
   */
  dispatcherMarketSelectionSubmitted: 'analytics:dispatcher:submitted',
  /**
   * Event triggered when a FAQ menu item is clicked.
   * This menu appears after you click on any section of the distributor page
   * located in https://www.bimbaylola.com/es_es/customer-service
   * @param {object} payload The payload of the event
   * @param {string} payload.item Section selected by clicking on link
   * @param {string} payload.option Subsection selected by clicking on link
   */
  faqsItemclicked: 'analytics:faqs:clicked',
  /**
   * Event triggered for a general error after a form validation
   * @param {object} payload The payload of the event
   * @param {string} payload.errorMessage - The message shown to visitor
   * @param {string} payload.formName - The name of the form
   * @param {PAGE_TYPES} payload.pageType - The type of the page where the form is
   */
  formErrorShown: 'analytics:form:error',
  /**
   * Event triggered for every field that is invalid after a validation
   * @param {object} payload The payload of the event
   * @param {string} payload.errorMessage - The message shown to visitor
   * @param {string} payload.formName - The name of the parent form of the field
   * @param {string} payload.label - The label of the field
   * @param {string} payload.name - The name of the form control
   * @param {PAGE_TYPES} payload.pageType - The type of the page where the form is
   */
  formFieldErrorShown: 'analytics:form-field:error',
  /**
   * Event emitted when the user adds a gift card to the cart
   * @param {object} payload - The payload of the event
   * @param {string} payload.type - Gift card design or type
   * @param {Types.AmountModel} payload.amount - Selected amount
   */
  giftCardAddedToCart: 'analytics:gift-card:added-to-cart',
  /**
   * Event emitted when the user checks the balance of a gift card
   * @param {object} payload - The payload of the event
   * @param {number} payload.balanceAmount - The numeric balance of the gift card
   * @param {string} payload.balanceCurrencyCode - The ISO currency code (e.g. "EUR")
   */
  giftCardBalanceChecked: 'analytics:gift-card-balance:checked',
  /**
   * Event emitted when the user selects with a gift card type and amount
   * @param {Object} payload - The payload of the event
   * @param {string} payload.type - Gift card design or type
   * @param {Types.AmountModel} payload.amount - Selected amount
   */
  giftCardSelected: 'analytics:gift-card:selected',
  /**
   * Event triggered when the forgot password form is opened.
   * @param {void} - No payload
   */
  loginForgotPasswordOpened: 'analytics:login-forgot-password-opened',
  /**
   * Event triggered when the forgot password form is sent.
   * @param {void} - No payload
   */
  loginForgotPasswordSent: 'analytics:login-forgot-password-sent',
  /**
   * Event triggered when the login tooltip of the header is closed.
   * @param {void} - No payload
   */
  loginTooltipClosed: 'analytics:login-tooltip-closed',
  /**
   * Event triggered when the login tooltip of the header is opened.
   * @param {void} - No payload
   */
  loginTooltipOpened: 'analytics:login-tooltip-opened',
  /**
   * Event triggered when user clicks on go back link of the menu (mobile only).
   * @param {string} item The parent category the user wants to return to
   * when go back link is clicked.
   */
  menuBackLinkClicked: 'menu:back-link-clicked',
  /**
   * Event triggered when user closes the menu by clicking on icon (mobile only).
   * @param {void} - No payload
   */
  menuCloseIconClicked: 'menu:icon-close-clicked',
  /**
   * Event triggered when user clicks on any category/subcategory of the menu.
   * @param {string} item The category/subcategory clicked
   */
  menuItemClicked: 'menu:item-clicked',
  /**
   * Event triggered when user opens the menu by clicking on icon (mobile only).
   * @param {void} - No payload
   */
  menuOpenIconClicked: 'menu:icon-open-clicked',
  /**
   * Event triggered when the cart recovery modal is closed.
   * @param {void} - No payload
   */
  modalCartRecoveryClosed: 'analytics:modal-cart-recovery-closed',
  /**
   * Event triggered when the cart recovery modal is shown.
   * @param {void} - No payload
   */
  modalCartRecoveryOpened: 'analytics:modal-cart-recovery-opened',
  /**
   * Event triggered when user is logged after the cart recovery modal is shown.
   * @param {string} referrer The referrer which triggers the event
   */
  modalCartRecoveryLogged: 'analytics:modal-cart-recovery-logged',
  /**
   * Event triggered when an option is clicked in the Global-e market redirect
   * modal.
   * @param {object} payload The payload of the event
   * @param {string} payload.selection The option selected: continue (stay on
   * the current site) or change country
   * @param {string} payload.site The name of the destination site
   */
  modalGlobalEOptionClicked: 'analytics:modal-global-e:clicked',
  /**
   * Event triggered when the redirect market modal is shown.
   * Two types exists:
   * 1. Modal for EU site: choose specific market from selector
   * 2. Modal for IC site: choose the alternative site or stay at the current one
   * @param {object} payload The payload of the event
   * @param {string} payload.type the type (alternative or specific)
   */
  modalRedirectMarketOpened: 'analytics:modal-redirect-market:opened',
  /**
   * Event triggered an option in redirect market modal is clicked.
   * Two types exists:
   * 1. Modal for EU site: choose specific market from selector
   * 2. Modal for IC site: choose the alternative site or stay at the current one
   * Different items can be clicked:
   * 1. Modal for IC site
   *  1.1 Change to alternative site (ES)
   *  1.2 Continue on current one
   * 2. Modal for EU site: continue to the site chosen in the selector
   * @param {object} payload The payload of the event
   * @param {string} payload.type the type (alternative or specific)
   * @param {string} payload.item the options offered before continuing
   * @param {string} payload.url target market url
   */
  modalRedirectMarketOptionClicked: 'analytics:modal-redirect-market:clicked',
  /**
   * Event triggered when Vertex invalid address modal is shown.
   * @param {void} - No payload
   */
  modalVertexInvalidAddressOpened: 'analytics:modal-vertex-invalid:opened',
  /**
   * Event triggered when Vertex suggested address modal is closed.
   * @param {void} - No payload
   */
  modalVertexSuggestedAddressClosed: 'analytics:modal-vertex-suggested:closed',
  /**
   * Event triggered when Vertex suggested address modal is confirmed.
   * @param {void} - No payload
   */
  modalVertexSuggestedAddressConfirmed:
    'analytics:modal-vertex-suggested:confirmed',
  /**
   * Event triggered when Vertex suggested address modal is edited.
   * @param {void} - No payload
   */
  modalVertexSuggestedAddressEdited: 'analytics:modal-vertex-suggested:edited',
  /**
   * Event triggered when Vertex suggested address modal is shown.
   * @param {void} - No payload
   */
  modalVertexSuggestedAddressOpened: 'analytics:modal-vertex-suggested:opened',
  /**
   * Event triggered when a country in worldwide selector modal is clicked
   * to navigate to it.
   * @param {object} payload The payload of the event
   * @param {string} payload.item the country the user navigates to
   */
  modalWorldwideSelectorClicked: 'analytics:modal-worldwide-selector:clicked',
  /**
   * Event triggered when the worldwide selector modal is shown.
   * @param {void} - No payload
   */
  modalWorldwideSelectorOpened: 'analytics:modal-worldwide-selector:opened',
  /**
   * Event triggered when a subscription to newsletter is made.
   * Different ways to subscribe are possible:
   * 1. Home Page: by submitting the Page Designer component form in Home page.
   * 2. Footer: by submitting the Footer component form.
   * 3. Landing Subscription Page: https://www.bimbaylola.com/es_es/newsletter-subscription
   * 4. Navigation Popup: user lands in any page and begins to navigate, specific custom
   * preference must be activated
   * 5. Register/Login Page: https://www.bimbaylola.com/es_es/cuenta/login?rurl=1
   * GDPR checkbox needs to be clicked when the register form is submitted
   * 6. Thank You Page: https://www.bimbaylola.com/es_es/order-confirm
   * GDPR checkbox needs to be clicked when the register form is submitted
   * 7. Thank You Page Popup: user finishes purchase, specific custom preference
   * must be activated
   * 8. Privacy section in My Account: email and accept policy checkboxes must be clicked
   * @param {string} location - The page or section where the subscription is made.
   */
  newsletterSubscribed: 'analytics:newsletter-subscribed',
  /**
   * Event triggered when a component is rendered and viewed for the first time.
   * @param {object} payload
   * @param {string} payload.page - The ID of a page of page designer
   * @param {PAGE_TYPES} payload.pageType - The type of the page
   * @param {string} payload.name - The name of component
   * @param {number} payload.position - The position of component in the page
   * @param {EXPERIENCE_COMPONENT_TYPES} payload.type - The type of component
   * @param {number} payload.clickedDescendantPosition - The position of clicked
   * descendant tile, if the component is an slider
   */
  pageDesignerComponentClicked: 'analytics:page-designer-component-clicked',
  /**
   * Event triggered when a component is rendered and viewed for the first time.
   * @param {object} payload
   * @param {string} payload.page - The ID of a page of page designer
   * @param {PAGE_TYPES} payload.pageType - The type of the page
   * @param {string} payload.name - The name of component
   * @param {number} payload.position - The position of component in the page
   * @param {EXPERIENCE_COMPONENT_TYPES} payload.type - The type of component
   */
  pageDesignerComponentImpressionViewed:
    'analytics:page-designer-component-viewed',
  /**
   * Event triggered when the configurable Page Designer popup is closed.
   * @param {object} payload The payload of the event
   * @param {string} payload.campaign - The name of the active promotional campaign
   *  (e.g. 'dia-de-la-madre', 'amigo-invisible')
   */
  pageDesignerPopUpClosed: 'analytics:page-designer-popup:closed',
  /**
   * Event triggered when the configurable Page Designer popup CTA is clicked.
   * @param {object} payload The payload of the event
   * @param {string} payload.campaign - The name of the active promotional campaign
   *  (e.g. 'dia-de-la-madre', 'amigo-invisible')
   */
  pageDesignerPopUpCtaClicked: 'analytics:page-designer-popup:cta-clicked',
  /**
   * Event triggered when the configurable Page Designer popup is shown.
   * @param {object} payload The payload of the event
   * @param {string} payload.campaign - The name of the active promotional campaign
   *  (e.g. 'dia-de-la-madre', 'amigo-invisible')
   */
  pageDesignerPopUpOpened: 'analytics:page-designer-popup:opened',
  /**
   * Event triggered when the page is loaded dynamically
   * @param {object} payload
   * @param {string} payload.pageTitle - page title
   * @param {PAGE_TYPES} payload.pageType - page type
   */
  pageLoadedDynamically: 'analytics:page-loaded-dynamically',
  /**
   * Event triggered after adding a Product to cart.
   * It has the product viewed as payload.
   * @param {object} payload The payload of the event
   * @param {Cart} payload.cart - The cart
   * @param {string} payload.context Section from which the product was added to cart
   * @param {string} payload.doubleLookPID The ID of the double look product
   * used to add to cart (ex: '241BZ0439.10000_DL137')
   * @param {string} [payload.list] The list type of the event
   * @param {number} [payload.position] The position of the product (zero-based)
   * @param {Types.FullProduct} payload.product The full product data
   * @param {ProductLineItem} payload.productLineItem The product line item
   * corresponding to the added product
   */
  productAddedToCart: 'product:added',
  /**
   * Event triggered when customize product is added to cart
   * @param {payload} payload The payload of the event
   * @param {string} payload.customizerId The ID of the product customizer
   */
  productCustomizerAddedToCart: 'analytics:product-customizer:added',
  /**
   * Event triggered when the edit button of the customizer is clicked
   * @param {payload} payload The payload of the event
   * @param {string} payload.customizerId The ID of the product customizer
   */
  productCustomizerEditionStarted:
    'analytics:product-customizer:edition-started',
  /**
   * Event triggered after loading a Product Detail Page.
   * @param {string} item Section link clicked
   */
  productDetailsItemClicked: 'the-product-details:item-clicked',
  /**
   * Event triggered after loading a Product Detail Page.
   * It has the product viewed as payload.
   * @param {object} payload The payload of the event
   * @param {Types.FullProduct} payload.product The full product data
   */
  productDetailsViewed: 'the-product-details:loaded',
  /**
   * Event triggered when a product (usually a tile) is viewed
   * @param {Object} payload The payload of the event
   * @param {Types.Category} [payload.category] If the location is a PLP Grid,
   *  the category of the ProductSearch can be sent
   * @param {string} payload.location The location where the tile is viewed
   * @param {string} payload.position The product position
   * @param {Types.FullProduct} payload.product The just-viewed product
   * @param {Object} [payload.recommender] Recommender info
   * @param {Object} [payload.recommender.name] Recommender name
   * @param {Object} [payload.recommender.isEinstein=false] True if the recommender is from Einstein
   */
  productImpressionViewed: 'analytics:product-impression-viewed',
  /**
   * Event triggered when a PLP banner image is clicked.
   * @param {object} payload The payload of the event
   * @param {string} payload.type The type of banner
   * @param {string} payload.url The URL of the main image link
   */
  productListBannerClicked: 'analytics:product-list:banner-clicked',
  /**
   * Event triggered when a PLP banner image is viewed.
   * @param {object} payload The payload of the event
   * @param {string} payload.type The type of banner
   * @param {number} payload.position The position of the banner inside the
   * list. If the position is unknown, the value is -1.
   * @param {string} [payload.url] The URL of the main image link if exists
   */
  productListBannerViewed: 'analytics:product-list:banner-viewed',
  /**
   * Event triggered when a PLP carousel (mobile only) element is clicked.
   * @param {object} payload The payload of the event
   * @param {string} payload.pid - The SKU of the product clicked
   * @param {number} payload.position Position for the product within the
   * carousel (zero-based)
   * @param {string} payload.type - The type of banner
   */
  productListBannerCarouselProductClicked:
    'analytics:product-list:banner-carousel-product-clicked',
  /**
   * Event triggered when a PLP carousel (mobile only) is viewed.
   * @param {object} payload The payload of the event
   * @param {number} payload.position The position of the banner inside the
   * list. If the position is unknown, the value is -1.
   * @param {string} payload.type The type of banner
   * @param {string} [payload.url] The URL of the main image link if exists
   */
  productListBannerCarouselViewed:
    'analytics:product-list:banner-carousel-viewed',
  /**
   * Event emitted when a category filter item is clicked
   * (before any filtering logic is applied)
   * @param {object} payload - The payload of the event
   * @param {string} payload.categoryName - Name of the clicked category
   */
  productListCategoryFilterClicked:
    'analytics:product-list:category-filter-clicked',
  /**
   * Event triggered when PLP column mode is changed.
   * @param {string} type Type of view selected to show the grid
   */
  productListColumnModeClicked: 'analytics:product-list:column-mode-clicked',
  /**
   * Event triggered when some filters are applied to PLP
   * @param {object} payload
   * @param {ProductSearchRefinementValue[]} payload.selectedFilters The filters applied
   * @param {SortingOption} payload.selectedSortingOption The sorting option applied
   */
  productListFiltersApplied: 'analytics:product-list:filters-applied',
  /**
   * Event triggered when the filters sidebar is closed
   */
  productListFiltersClosed: 'analytics:product-list:filters-closed',
  /**
   * Event triggered when the filters sidebar is opened
   */
  productListFiltersOpened: 'analytics:product-list:filters-opened',
  /**
   * Event triggered when the filters are reset
   */
  productListFiltersReset: 'analytics:product-list:filters-reset',
  /**
   * Event triggered when mounting or the new products are added to the PLP.
   * @param {Object} payload
   * @param {Object} payload.category Current category
   * @param {Object[]} payload.products Products listed
   * @param {Object} payload.products.display Display config for a product
   * @param {Types.FullProduct} payload.products.product Products listed
   * @param {Object} payload.products.urls URLS of the product
   */
  productListViewed: 'analytics:product-list:loaded',
  /**
   * Event triggered when the target category does not return any product
   * @param {Object} payload
   * @param {Object} payload.category Current category
   */
  productListViewedEmpty: 'analytics:product-list:viewed-empty',
  /**
   * Event triggered after removing a Product from cart.
   * It has the product viewed as payload.
   * @param {object} payload The payload of the event
   * @param {Cart} payload.cart The cart
   * @param {Types.FullProduct} payload.product Product variant removed
   */
  productRemovedFromCart: 'product:removed',
  /**
   * Event triggered when a product slider has been loaded and its products are
   * fully rendered.
   * @param {Object} payload
   * @param {Object[]} payload.products Products listed
   * @param {Object} payload.products.display Display config for a product
   * @param {Types.FullProduct} payload.products.product Products listed
   * @param {Object} payload.products.urls URLS of the product
   * @param {Types.BaseProductTileContext} payload.tileContext Context of the
   *  BaseProductTiles of the slider
   */
  productSliderReady: 'analytics:product-slider:ready',
  /**
   * Event triggered when the children products of a product slider are updated.
   * Triggered too when the slider is initializing.
   * @param {Object} payload The payload of the event
   * @param {Object[]} payload.products Products listed
   * @param {Object} payload.products.display Display config for a product
   * @param {Types.FullProduct} payload.products.product Products listed
   * @param {Object} payload.products.urls URLS of the product
   * @param {Types.BaseProductTileContext} payload.tileContext Context of the
   *  BaseProductTiles of the slider
   */
  productSliderUpdated: 'analytics:product-slider:updated',
  /**
   * Event triggered after clicking a Product Tile.
   * @param {object} payload The payload of the event
   * @param {Types.Category} [payload.category] If the location is a PLP Grid,
   *  the category of the ProductSearch can be sent
   * @param {string} payload.location The location where the tile is clicked
   * @param {string} [payload.position] The product position when applies
   * @param {Types.FullProduct} payload.product Target product
   * @param {Object} [payload.recommender] Recommender info
   * @param {Object} [payload.recommender.name] Recommender name
   * @param {Object} [payload.recommender.isEinstein=false] True if the recommender is from Einstein
   */
  productTileClicked: 'product-tile:clicked',
  /**
   * Event triggered after clicking the quickshop button on a Product Tile.
   * @param {object} payload The payload of the event
   * @param {string} payload.location The location where the tile is clicked
   * @param {string} payload.productId The product SKU
   * @param {Object} [payload.recommender] Recommender info
   * @param {Object} [payload.recommender.name] Recommender name
   * @param {Object} [payload.recommender.isEinstein=false] True if the recommender is from Einstein
   */
  productTileQuickshopClicked: 'product-tile:quickshop-clicked',
  /**
   * Event triggered when the search form and the results are closed
   * @param {void} - No payload
   */
  searchHidden: 'analytics:search:hidden',
  /**
   * Event triggered when any most searched terms (in Search AI Modal) is clicked
   * @param {string} term The term clicked by user
   */
  searchMostSearchedClicked: 'analytics:search:most:clicked',
  /**
   * Event triggered when a query is inserted in search input
   * @param {object} payload
   * @param {string} payload.search.query The search term introduced by user
   */
  searchQueryInserted: 'analytics:search:query:inserted',
  /**
   * Event triggered when any recommended product (in Search AI Modal) is clicked
   * @param {Object} payload The payload of the event
   * @param {Object} payload.product Data related to product
   * @param {string} payload.product.pid Product SKU
   * @param {number} payload.position The position in carousel
   * @param {string} payload.location The itemList (Search)
   * @param {string} payload.componentType The type of component
   * @param {Object} [payload.recommender] Recommender info
   * @param {Object} [payload.recommender.name] Recommender name
   * @param {Object} [payload.recommender.isEinstein=false] True if the recommender is from Einstein
   */
  searchRecommendedProductClicked: 'analytics:search-modal:recommended:clicked',
  /**
   * Event triggered when any recommended product (in Search AI Modal) is viewd
   * @param {Object} payload The payload of the event
   * @param {Object} payload.product Data related to product
   * @param {string} payload.product.pid Product SKU
   * @param {number} payload.position The position in carousel
   * @param {string} payload.location The itemList (Search)
   * @param {string} payload.componentType The type of component
   * @param {Object} [payload.recommender] Recommender info
   * @param {Object} [payload.recommender.name] Recommender name
   * @param {Object} [payload.recommender.isEinstein=false] True if the recommender is from Einstein
   */
  searchRecommendedProductViewed: 'analytics:search-modal:recommended:viewed',
  /**
   * Event triggered when a search does not found any product
   * @param {object} search
   * @param {string} search.query The search term introduced by user
   * @param {string} search.match The match of the search query
   * @param {boolean} search.isCached True if the search results comes from
   * client's cache
   */
  searchReturnedNoResults: 'analytics:search:returned-no-results',
  /**
   * Event triggered when a search returned some results
   * @param {object} payload
   * @param {object} payload.search Data related to search
   * @param {string} payload.search.query The search term introduced by user
   * @param {string} payload.search.match The match of the search query
   * @param {number} payload.search.count The number of results
   * @param {number} payload.search.page The page number
   * @param {boolean} payload.search.isCached True if the search results comes
   * from client's cache
   * @param {object[]} payload.products Products listed
   * @param {object} payload.products.display Display config for a product
   * @param {jsdocTypes.FullProduct} payload.products.product Products listed
   * @param {object} payload.products.urls URLS of the product
   * @param {boolean} payload.isPageView True if it is the first tracking event
   * of the search (ex: when closing the pdp modal in mobile we consider a new
   * search view)
   */
  searchReturnedResults: 'analytics:search:returned-results',
  /**
   * Event triggered when user clicks on search icon (hand lens)
   * @param {string} type Search engine type [gaInfo.searchEngineType]
   */
  searchSessionUsed: 'analytics:search:session',
  /**
   * Event triggered when the search form is viewed
   * @param {void} - No payload
   */
  searchShown: 'analytics:search:shown',
  /**
   * Event triggered when search AI tooltip is loaded
   * @param {string} item Tooltip is loaded or clicking on CTA button/cross for hiding
   */
  searchTooltipLoaded: 'analytics:search:tooltip',
  /**
   * Event triggered when PDP stock alert form modal is closed (mobile only).
   * Also triggered in PLP when an out of stock size is clicked
   * after clicking in quick shop button (mobile only).
   * @param {void} - No payload
   */
  stockAlertClosed: 'analytics:stock-alert-closed',
  /**
   * Event triggered when PDP stock alert form modal is opened.
   * Also triggered in PLP when an out of stock size is clicked
   * after clicking in quick shop button (mobile only).
   * @param {void} - No payload
   */
  stockAlertOpened: 'analytics:stock-alert-opened',
  /**
   * Event triggered when PDP stock alert form modal is sent.
   * Also triggered in PLP when an out of stock size is clicked
   * after clicking in quick shop button (mobile only).
   * @param {void} - No payload
   */
  stockAlertSent: 'analytics:stock-alert-sent',
  /**
   * Event triggered when top banner is clicked.
   * It's dispatched within the content asset 'header-banner-web'
   * @param {object} payload The payload of the event
   * @param {string} payload.item URL where the top banner is displayed
   */
  topBannerClicked: 'topBanner:clicked',
  /**
   * Event triggered after the navigation guards have finished. Just before
   * the DOM updates are triggered to show the new page
   * @param {object} payload
   * @param {import("vue-router").Route} payload.to - route location
   * @param {import("vue-router").Route} payload.from - route location
   */
  userWillNavigateDinamically: 'analytics:user:will-navigate',
  /**
   * Event triggered when virtual try-on is clicked.
   * @param {void} - No payload
   */
  virtualTryOnClicked: 'virtualTryOn:clicked',
  /**
   * Event triggered when a whatsapp chat is opened.
   * @param {string} location - The page where is opened ('Contacto' o 'Producto')
   */
  whatsappOpened: 'whatsapp:open',
  /**
   * Event triggered when login link is clicked in Wishlist.
   * @param {void} - No payload
   */
  wishlistLoginLinkClicked: 'wishlist:login:clicked',
  /**
   * Event triggered after adding a Product to the wishlist.
   * It has the added product as payload.
   * @param {object} payload The payload of the event
   * @param {Types.FullProduct} payload.product The full product data
   */
  wishlistProductAdded: 'wishlist:product:added',
  /**
   * Event triggered when a product is removed from the Wishlist.
   * There are 2 places where we can do it:
   * 1. Wishlist tab: it happens when trash icon is clicked in minicart.
   * 2. Cart tab: it happens when clicking on "Added to wishlist" button.
   * @param {void} - No payload
   */
  wishlistProductRemoved: 'wishlist:product:removed',
  /**
   * Event triggered when the share button of the Wishlist is clicked.
   * @param {void} - No payload
   */
  wishlistShareButtonClicked: 'wishlist:share:clicked',
};

const PAYLOADS = (function buildPayloadWithPrivateCommonConstants() {
  const common = {
    productListBannerType: {
      bannerImageDouble: 'double',
      bannerImageFull: 'full',
      bannerImageSimple: 'simple',
      bannerCarousel: 'mobileSlider',
      bannerVideoSimple: 'videoSimple',
      bannerVideoDouble: 'videoDouble',
    },
    productTileLocation: PRODUCT_TILE_LOCATION,
  };

  return {
    accessibilityIconClicked: {
      action: {
        close: 'close',
        open: 'open',
      },
    },
    accountRegistered: {
      location: {
        account: 'account',
        checkout: 'checkout',
        thankYouPage: 'thank-you-page',
      },
    },
    atcPhoneClicked: {
      location: {
        cart: 'cart',
        checkout: 'checkout',
        minicart: 'minicart',
        storeAvailability: 'store_availability',
      },
    },
    checkoutOrderSummaryEdited: {
      item: {
        billing: 'billing_address',
        payment: 'payment_method',
        shipping: 'shipping_method',
      },
    },
    faqsItemclicked: {
      section: 'primary',
    },
    formFieldErrorShown: {
      pageType: PAGE_TYPES,
    },
    modalGlobalEOptionClicked: {
      selection: {
        continue: 'continue',
        change: 'change_country',
      },
    },
    modalRedirectMarket: {
      item: {
        continue: 'continue',
        change: 'change_to_spain',
      },
      site: {
        es: 'ES',
        ic: 'IC',
      },
      type: {
        eu: 'europe',
        ic: 'canary_islands',
      },
    },
    newsletterSubscribed: {
      location: {
        footer: 'footer',
        home: 'home',
        landingSubscribe: 'landing-subscribe',
        popup: 'popup',
        privacy: 'privacy',
        register: 'register',
        thankYouPage: 'thank-you-page',
        thankYouPagePopup: 'thank-you-page-popup',
      },
    },
    pageDesigner: {
      pageType: {
        [`${PAGE_TYPES.home}`]: 'home_section',
        [`${PAGE_TYPES.searchModal}`]: 'search_sections',
      },
    },
    productAddedToCart: {
      context: {
        cart: 'cart',
        category: 'category',
        crossSellingGrid: 'related_category',
        product: 'product',
        search: 'search',
        shopTheLook: 'shop_the_look',
      },
    },
    productCustomizerEditionStarted: {
      customizerId: FRONT_CONFIG.customizer.ids,
    },
    productDetailsItemClicked: {
      item: {
        chat: 'chat',
        deliveryReturns: 'delivery_returns',
        details: 'details',
        fitAnalytics: 'fit_analytics',
        inStoreStock: 'store_availability',
        paymentMethod: 'payment_method',
        share: 'share',
        sizeGuide: 'size_guide',
      },
    },
    productImpressionViewed: {
      location: common.productTileLocation,
    },
    productListBannerClicked: {
      type: common.productListBannerType,
    },
    productListBannerViewed: {
      type: common.productListBannerType,
    },
    productListBannerCarouselProductClicked: {
      type: common.productListBannerType,
    },
    productListBannerCarouselViewed: {
      type: common.productListBannerType,
    },
    productListColumnModeClicked: {
      columnMode: {
        compressedView: 'grid_big_view',
        defaultView: 'grid_small_view',
      },
    },
    productTileClicked: {
      location: common.productTileLocation,
    },
    searchedTermNoText: {
      type: {
        featured: 'no_search_term_fixed_category',
        image: 'no_search_term_camera',
        interested: 'no_search_term_may_be_of_interest',
      },
    },
    searchTooltipLoaded: {
      item: {
        cross: 'cross',
        cta: 'buscar',
        view: 'view',
      },
    },
  };
})();

/**
 * Events for collecting analytic information
 * @readonly
 * @enum {string}
 */
const ANALYTICS_EVENTS = {
  ...EVENTS,
  PAYLOADS,
};

const analyticsEventBus = createEventBus('analytics', {
  events: EVENTS,
  payloads: PAYLOADS,
});

const exported = {
  ANALYTICS_EVENTS,
  analyticsEventBus,
};

/** @type {exported} */
module.exports = generateSingletonExports('analytics-events', exported);
